import { Footer, AppBar } from '@sendhub/integrations-ui'

import { Route, Routes, Navigate, useLocation } from 'react-router-dom'
import { Box, CssBaseline, ScopedCssBaseline, useTheme } from '@mui/material'
import { useSelector } from 'react-redux'
import { logout, selectRefreshToken } from './store/auth/auth.slice'
import { useAppDispatch } from './store/root'
import { Fragment, useEffect, useState } from 'react'
import { fetchIntegrationGroups } from './store/integration-group/integration-groups.slice'
import { fetchIntegrations } from './store/integration/integrations.slice'
import { fetchIntegrationConfigs } from './store/integration-config/integration-configs.slice'
import { fetchServiceAccounts } from './store/service-account/service-accounts.slice'
import { AllIntegrationsBackButton, IntegrationGroupDetailsPage, ListOfIntegrationGroups, LoginPage, TabPanel } from './components/pages'
import { fetchUsers, selectProfile } from './store/sendhub-users/sendhub-users.slice'
import { ZapierOAuthPage } from './components/pages/zapier-oauth'
import { fetchZapierApps } from './store/zapier/zapier-apps.slice'
import { fetchZapierTemplates } from './store/zapier/zapier-templates.slice'
import { ToastContainer, toast } from 'react-toastify'
import 'react-toastify/dist/ReactToastify.css'
import { AsyncTest } from './components/pages/async-test'
import { INTEGRATIONS_BASE_URL } from '../utils/config'
import { getEmbedConfig } from '../utils/embed-config'

// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
window.toast = toast

export function App() {
  const isLoggedIn = !!useSelector(selectRefreshToken)
  const location = useLocation()
  const profile = useSelector(selectProfile)
  const [hideHeaderFooter, setHideHeaderFooter] = useState(false)
  // const isLoggedIntoZapier = !!useSelector(selectZapierProfileAccessToken)
  const dispatch = useAppDispatch()
  const theme = useTheme()
  const embedded = getEmbedConfig().embedded
  const Baseline = embedded ? ScopedCssBaseline : Fragment

  const state = useSelector((s) => s)

  // We need to check auth here since there are some routes that don't force
  //  the login page first
  // useEffect(() => {
  //   if (!isLoggedIn) {
  //     dispatch(checkAuth())
  //   }
  // }, [])

  useEffect(() => {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    window.state = state
  }, [state])

  useEffect(() => {
    console.log('logged in as:', profile?.username)
  }, [profile])

  // Fetch all data
  useEffect(() => {
    if (isLoggedIn) {
      dispatch(fetchZapierTemplates())
      dispatch(fetchIntegrationGroups())
      dispatch(fetchIntegrations())
      dispatch(fetchIntegrationConfigs())
      dispatch(fetchServiceAccounts())
      dispatch(fetchUsers())
      dispatch(fetchZapierApps())
    }
  }, [isLoggedIn, dispatch])

  // The single source of truth for which message types this app handles. Keyed
  // by type so there's no separate allowlist that can drift out of sync with
  // the handlers themselves.
  const messageHandlers: Record<string, () => void> = {
    fetchServiceAccounts: () => {
      console.log('received fetchServiceAccounts message - fetching ServiceAccounts...')
      dispatch(fetchServiceAccounts())
    },
    pink: () => {
      document.querySelectorAll('div').forEach((el) => (el.style.backgroundColor = 'pink'))
    },
  }

  function handleMessageEvent(event: MessageEvent) {
    // This listener is global to the whole page, so it hears every postMessage
    // the browser receives - not just ones meant for us. Bail out silently on
    // anything that isn't even shaped like a message (avoids throwing on
    // `'type' in null`, etc.) before we look at origin or type at all.
    if (typeof event.data !== 'object' || event.data === null || !('type' in event.data)) return

    const { type } = event.data

    // Verify the message came from a trusted origin. Our bundle is injected
    // directly into the host page's DOM (not an iframe), so bare `origin` here
    // is the *host's* origin - but the Zapier OAuth popup is served from and
    // posts back from this app's own origin (INTEGRATIONS_BASE_URL), a
    // different origin on staging/production. Trust that origin explicitly
    // rather than the host's.
    const trustedOrigins = [new URL(INTEGRATIONS_BASE_URL).origin]
    if (!trustedOrigins.includes(event.origin)) {
      // Only log for messages that actually claim to be ours - unrelated
      // background traffic (ads, analytics, etc.) never gets logged at all,
      // but a message that impersonates one of our own types from the wrong
      // origin is a real signal worth flagging.
      if (type in messageHandlers) {
        console.error('Message received from untrusted origin')
      }
      return
    }

    // The message is from our own trusted popup - an unrecognized type here
    // means something on our end broke (a typo, or a new type that was never
    // wired up), which is worth surfacing rather than silently dropping.
    const handler = messageHandlers[type]
    if (handler) handler()
    else console.error('Unknown message type received')
  }

  useEffect(() => {
    window.addEventListener('message', handleMessageEvent)
    return () => {
      window.removeEventListener('message', handleMessageEvent)
    }
  }, [handleMessageEvent])

  // If iframe=1 in query params, then hide header and footer
  useEffect(() => {
    const currentHost = new URL(window.location.href).host

    const queryParams = Object.fromEntries(new URLSearchParams(window.location.search).entries())
    if (('iframe' in queryParams && queryParams['iframe']) || (!currentHost.includes('int') && !currentHost.includes('localhost'))) {
      setHideHeaderFooter(true)
    }
  }, [location])

  return (
    <>
      <style>
        {`:root {
          --toastify-color-progress-light: ${theme.palette.primary.main};
        }`}
      </style>
      <ToastContainer />
      {/* A host page owns <body>: global resets would restyle it, so scope them. */}
      {!embedded && <CssBaseline />}
      <Baseline>
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            minHeight: embedded ? '100%' : '100vh',
          }}
        >
          <div style={{ flex: '1 0 auto ' }}>
            {!hideHeaderFooter && (
              <AppBar onLogOut={isLoggedIn ? () => dispatch(logout()) : undefined} integrationsBaseUrl={INTEGRATIONS_BASE_URL} />
            )}
            <Routes>
              <Route path="/signin" element={<LoginPage />} />
              <Route path="/integrations" element={<RequireAuth children={<ListOfIntegrationGroups />} />} />
              <Route path="/AsyncTest" element={<RequireAuth children={<AsyncTest />} />} />
              <Route
                path="/integrations/zapier"
                element={
                  <RequireAuth
                    children={
                      <Box sx={{ p: 4, paddingTop: 0 }}>
                        <AllIntegrationsBackButton />
                        <h1>Zapier Elements React</h1>
                        <zapier-full-experience
                          client-id="98T3iTfdGfdKZD1Ye4lIg79Klv0VMbIf1deGfEED"
                          theme="light"
                          app-search-bar-display="show"
                          app-exclusions="sms,clicksend,textmagic,salesmsg,simpletexting,ez-texting,textedly"
                          app-categories="-phone"
                          zap-call-to-action-display="show"
                          zap-create-from-scratch-display="show"
                        />
                      </Box>
                    }
                  />
                }
              >
                <Route path=":integrationSlug" element={<RequireAuth children={<TabPanel />} />} />
              </Route>
              <Route path="/integrations/:integrationGroupId" element={<RequireAuth children={<IntegrationGroupDetailsPage />} />}>
                <Route path=":integrationSlug" element={<RequireAuth children={<TabPanel />} />} />
              </Route>
              <Route path="/zapier-oauth" element={<RequireAuth children={<ZapierOAuthPage />} />} />
              <Route path="*" element={<Navigate to="/integrations" replace />} />
            </Routes>
          </div>
          {!hideHeaderFooter && <Footer />}
        </div>
      </Baseline>
    </>
  )
}

export function RequireAuth({ children }: { children: JSX.Element }) {
  const isLoggedIn = !!useSelector(selectRefreshToken)
  const location = useLocation()

  if (isLoggedIn) return children

  // Redirect them to the /signin page, but save the current location they were
  // trying to go to when they were redirected. This allows us to send them
  // along to that page after they login, which is a nicer user experience
  // than dropping them off on the home page.
  return <Navigate to="/signin" state={{ from: location, queryParams: location.search, hash: location.hash }} />
}

export default App
